import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faSignInAlt, faCheck, faTimes, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

const Login: React.FC = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<number | null>(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const { login, selectOrganization } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if device is mobile
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check for error messages from URL parameters (after redirect)
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      // Clear the error parameter from URL
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await login(credentials);
      console.log('Login response:', response);
      
      if (response.success && response.organizations && response.organizations.length > 1) {
        // User has multiple organizations, show selection modal
        setOrganizations(response.organizations);
        setShowOrgModal(true);
      } else if (response.success) {
        // Single organization or direct login successful
        console.log('Login successful, navigating to dashboard');
        navigate('/dashboard');
      } else {
        console.log('Login failed, error:', response.error);
        setError(response.error || 'Ungültige Anmeldedaten. Bitte überprüfen Sie Ihre Eingaben.');
        setLoading(false);
      }
    } catch (error: any) {
      console.log('Login exception:', error);
      setError(error.message || 'Anmeldung fehlgeschlagen');
      setLoading(false);
    }
  };

  const handleOrgSelection = async () => {
    if (!selectedOrg) return;
    
    setOrgLoading(true);
    const response = await selectOrganization(selectedOrg);
    if (response.success) {
      setShowOrgModal(false);
      navigate('/dashboard');
    } else {
      setError(response.error || 'Organisationsauswahl fehlgeschlagen');
    }
    setOrgLoading(false);
  };

  const handleCancelOrgSelection = () => {
    setShowOrgModal(false);
    setOrganizations([]);
    setSelectedOrg(null);
    setError('');
  };

  return (
    <>
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        fontFamily: 'Arial, sans-serif',
        overflow: 'hidden'
      }}>
      {/* Left Section - Hero Background - Hidden on mobile */}
      {!isMobile && (
        <div style={{
          flex: '1',
          background: 'url("/firefighter-bg.png") center/cover',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
          {/* Dark overlay for better text readability - only on left section */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0.8) 100%)',
            zIndex: 1
          }} />
          
          {/* Content */}
          <div style={{
            textAlign: 'center',
            color: 'white',
            zIndex: 2,
            position: 'relative'
          }}>
            <div style={{
              fontSize: '48px',
              fontWeight: 'bold',
              marginBottom: '20px',
              textShadow: '2px 2px 8px rgba(0,0,0,0.7)',
              letterSpacing: '2px',
              fontFamily: '"ITC Avant Garde Gothic", "ITC Avant Garde Gothic Bold", "Avant Garde", Arial, sans-serif'
            }}>
              ASMONITOR
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '10px',
              textShadow: '1px 1px 4px rgba(0,0,0,0.7)',
              letterSpacing: '1px',
              fontFamily: '"ITC Avant Garde Gothic", "ITC Avant Garde Gothic Bold", "Avant Garde", Arial, sans-serif'
            }}>
              DEIN DIGITALER
            </div>
            <div style={{
              fontSize: '28px',
              fontWeight: 'bold',
              marginBottom: '20px',
              textShadow: '1px 1px 4px rgba(0,0,0,0.7)',
              letterSpacing: '1px',
              fontFamily: '"ITC Avant Garde Gothic", "ITC Avant Garde Gothic Bold", "Avant Garde", Arial, sans-serif'
            }}>
              TRUPP ÜBERWACHER
            </div>
            <div style={{
              fontSize: '18px',
              fontWeight: '300',
              opacity: 0.95,
              textShadow: '1px 1px 4px rgba(0,0,0,0.7)',
              letterSpacing: '1px',
              fontFamily: '"ITC Avant Garde Gothic", "ITC Avant Garde Gothic Bold", "Avant Garde", Arial, sans-serif'
            }}>
              SCHNELLER • EINFACHER • SICHERER
            </div>
          </div>
          
          {/* Decorative Elements */}
          <div style={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            fontSize: '12px',
            opacity: 0.8,
            color: 'white',
            zIndex: 2,
            textShadow: '1px 1px 2px rgba(0,0,0,0.7)'
          }}>
            www.as-monitor.ch
          </div>
        </div>
      )}

      {/* Right Section - Login Form */}
      <div style={{
        flex: isMobile ? '1 1 100%' : '1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isMobile ? 'white' : '#f8f9fa',
        position: 'relative',
        zIndex: 10
      }}>
        {/* White curved shape overlay - Hidden on mobile */}
        {!isMobile && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: '-50px',
            right: 0,
            bottom: 0,
            background: 'white',
            borderRadius: '50px 0 0 0',
            boxShadow: '-10px 0 30px rgba(0,0,0,0.1)',
            zIndex: 1
          }} />
        )}
        
        <div style={{
          maxWidth: '400px',
          width: '100%',
          margin: isMobile ? '0 20px' : '0 60px',
          zIndex: 2,
          position: 'relative'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '40px'
          }}>
            <div style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#c00',
              marginBottom: '10px'
            }}>
              Willkommen
            </div>
            <div style={{
              fontSize: '16px',
              color: '#666',
              lineHeight: '1.5'
            }}>
              Melde dich an, um auf das ASMonitor Portal zuzugreifen
            </div>
          </div>
          
          {error && (
            <div style={{
              backgroundColor: '#ffebee',
              color: '#c00',
              padding: '16px',
              borderRadius: '14px',
              marginBottom: '24px',
              fontSize: '14px',
              border: '1px solid #ffcdd2',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '16px' }} />
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} style={{ marginBottom: '24px' }}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: '#333',
                fontWeight: '500',
                fontSize: '14px'
              }}>
                Benutzername
              </label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                style={{
                  width: '100%',
                  padding: '16px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '14px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.3s ease',
                  backgroundColor: 'white'
                }}
                onFocus={(e) => e.target.style.borderColor = '#c00'}
                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
                required
                disabled={loading}
                placeholder="Ihr Benutzername"
              />
            </div>
            
            <div style={{ marginBottom: '32px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: '#333',
                fontWeight: '500',
                fontSize: '14px'
              }}>
                Passwort
              </label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                style={{
                  width: '100%',
                  padding: '16px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '14px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.3s ease',
                  backgroundColor: 'white'
                }}
                onFocus={(e) => e.target.style.borderColor = '#c00'}
                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
                required
                disabled={loading}
                placeholder="Ihr Passwort"
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: loading ? '#ccc' : '#c00',
                color: '#ffffff',
                border: 'none',
                borderRadius: '14px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: loading ? 'none' : '0 4px 12px rgba(201, 31, 12, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(201, 31, 12, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(201, 31, 12, 0.3)';
                }
              }}
            >
              <FontAwesomeIcon icon={faSignInAlt} style={{ fontSize: '16px' }} />
              {loading ? 'Anmelden...' : 'Anmelden'}
            </button>
          </form>

          <div style={{
            textAlign: 'center',
            fontSize: '12px',
            color: '#999',
            lineHeight: '1.4'
          }}>
            Sichere Anmeldung über verschlüsselte Verbindung
          </div>
        </div>
      </div>

        {/* Organization Selection Modal */}
        {showOrgModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '40px',
              minWidth: '450px',
              maxWidth: '550px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              border: '1px solid rgba(201, 31, 12, 0.1)'
            }}>
              <div style={{
                textAlign: 'center',
                marginBottom: '32px'
              }}>
                <div style={{
                  fontSize: '28px',
                  fontWeight: 'bold',
                  color: '#c00',
                  marginBottom: '8px'
                }}>
                  Organisation auswählen
                </div>
                <div style={{
                  fontSize: '16px',
                  color: '#666',
                  lineHeight: '1.5'
                }}>
                  Du gehörst mehreren Organisationen an. Bitte wähle die Organisation aus, für die du arbeiten möchtest.
                </div>
              </div>
              
              <div style={{ 
                width: '100%', 
                marginBottom: '32px',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {organizations.map((org) => (
                  <div
                    key={org.orgID}
                    style={{
                      padding: '16px 20px',
                      border: selectedOrg === org.orgID ? '2px solid #c00' : '2px solid #e1e5e9',
                      borderRadius: '12px',
                      marginBottom: '12px',
                      cursor: 'pointer',
                      backgroundColor: selectedOrg === org.orgID ? '#fff5f5' : '#fff',
                      transition: 'all 0.3s ease',
                      boxShadow: selectedOrg === org.orgID ? '0 4px 12px rgba(201, 31, 12, 0.15)' : '0 2px 4px rgba(0,0,0,0.05)'
                    }}
                    onClick={() => setSelectedOrg(org.orgID)}
                    onMouseEnter={(e) => {
                      if (selectedOrg !== org.orgID) {
                        e.currentTarget.style.borderColor = '#c00';
                        e.currentTarget.style.backgroundColor = '#fff9f9';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedOrg !== org.orgID) {
                        e.currentTarget.style.borderColor = '#e1e5e9';
                        e.currentTarget.style.backgroundColor = '#fff';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    <div style={{ 
                      fontWeight: '600', 
                      fontSize: '16px',
                      color: selectedOrg === org.orgID ? '#c00' : '#333'
                    }}>
                      {org.fdName || org.orgName || org.name}
                    </div>
                  </div>
                ))}
              </div>
              
              <div style={{ 
                display: 'flex', 
                gap: '16px', 
                width: '100%' 
              }}>
                <button
                  onClick={handleCancelOrgSelection}
                  disabled={orgLoading}
                  style={{
                    flex: 1,
                    background: '#666',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '14px',
                    padding: '16px',
                    fontWeight: '600',
                    cursor: orgLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    fontSize: '16px'
                  }}
                  onMouseEnter={(e) => {
                    if (!orgLoading) {
                      e.currentTarget.style.backgroundColor = '#555';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!orgLoading) {
                      e.currentTarget.style.backgroundColor = '#666';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} style={{ marginRight: '8px' }} />
                  Abbrechen
                </button>
                <button
                  onClick={handleOrgSelection}
                  disabled={!selectedOrg || orgLoading}
                  style={{
                    flex: 1,
                    background: selectedOrg && !orgLoading ? '#c00' : '#ccc',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '14px',
                    padding: '16px',
                    fontWeight: '600',
                    cursor: selectedOrg && !orgLoading ? 'pointer' : 'not-allowed',
                    transition: 'all 0.3s ease',
                    fontSize: '16px',
                    boxShadow: selectedOrg && !orgLoading ? '0 4px 12px rgba(201, 31, 12, 0.3)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedOrg && !orgLoading) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(201, 31, 12, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedOrg && !orgLoading) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(201, 31, 12, 0.3)';
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '8px' }} />
                  {orgLoading ? 'Bestätigen...' : 'Bestätigen'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Login; 